import BorrowingResponsibilityConfirmation from "@/components/borrowing/BorrowingResponsibilityConfirmation";
import SelectionFilter from "@/components/multi-select-filter/SelectionFilter";
import { Badge } from "@/components/ui/badge/Badge";
import { Button } from "@/components/ui/button/Button";
import { Calendar } from "@/components/ui/calendar/Calendar";
import { Label } from "@/components/ui/label/Label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select/Select";
import RoomLocationService from "@/lib/services/room/RoomLocationService";
import RoomService from "@/lib/services/room/RoomService";
import ShiftService from "@/lib/services/shift/ShiftService";
import {
  convertShiftsToOptions,
  resolveBorrowerCredentials,
} from "@/lib/utils";
import IAsset from "@/models/asset/Asset";
import { IRoom } from "@/models/room/Room";
import { IRoomLocation } from "@/models/room/RoomLocation";
import { IShift } from "@/models/shift/Shift";
import { useUserStore } from "@/store/user/userStore";
import { motion } from "framer-motion";
import { BookOpenCheck } from "lucide-react";
import { useCallback, useEffect, useState } from "react";
import { isMatch, Matcher } from "react-day-picker";
import ChooseAssetsDialog, { FetchAssetsParams } from "./ChooseAssetsDialog";
import { Input } from "@/components/ui/input/Input";
import { useToast } from "@/lib/hooks/useToast";
import { UserFacingError } from "@/models/helper/UserFacingError";
import AssetBorrowingService from "@/lib/services/asset-borrowing/AssetBorrowingService";
import { PaginatedResponse } from "@/models/helper/Response";
import { AvailableShiftsResponse } from "@/models/shift/AvailableShiftsResponse";

const initialFormData = {
  locationId: null as number | null,
  room: null as IRoom | null,
  pickupDate: undefined as Date | undefined,
  returnDate: undefined as Date | undefined,
  pickupShift: null as IShift | null,
  returnShift: null as IShift | null,
  assets: [] as IAsset[],
  purpose: "",
  borrowerPhoneNumber: "",
  leaderEmailOrBinusianId: "",
  isResponsibilityConfirmed: false,
};

type Props = {
  setLoading: React.Dispatch<React.SetStateAction<boolean>>;
};

const InsertAssetBorrowingForm = ({ setLoading }: Props) => {
  const { toast } = useToast();
  const userStore = useUserStore();
  const [locations, setLocations] = useState<IRoomLocation[]>([]);
  const [rooms, setRooms] = useState<IRoom[]>([]);
  const [pickupShiftsResponse, setPickupShiftsResponse] =
    useState<AvailableShiftsResponse>({ shifts: [], room_capacity: 0 });
  const [returnShiftsResponse, setReturnShiftsResponse] =
    useState<AvailableShiftsResponse>({ shifts: [], room_capacity: 0 });
  const [holidays, setHolidays] = useState<Matcher[]>([{ dayOfWeek: [0] }]);
  const [changeAssetsDialogOpen, setChangeAssetsDialogOpen] = useState(false);
  const [formData, setFormData] = useState(initialFormData);

  useEffect(() => {
    const fetchLocations = async () => {
      setLocations(await RoomLocationService.getAssetBorrowingLocations());
    };

    const fetchHolidays = async () => {
      const holidays: Date[] = await ShiftService.getAllHoliday(
        new Date().getFullYear().toString()
      );

      setHolidays((prev) => [...prev, ...holidays]);
    };

    const fetchData = async () => {
      setLoading(true);
      try {
        await Promise.all([fetchLocations(), fetchHolidays()]);
      } catch (error) {
        console.error("Failed to fetch initial data:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [setLoading]);

  useEffect(() => {
    const fetchRooms = async () => {
      if (formData.locationId === null) return;
      setLoading(true);
      setRooms(
        await RoomService.getAssetBorrowingRooms(
          formData.locationId,
          userStore.data?.Email ?? "",
          "Assistant"
        )
      );
      setLoading(false);
    };

    setFormData((prev) => ({ ...prev, room: null, assets: [] }));
    fetchRooms();
  }, [formData.locationId, setLoading, userStore.data?.Email]);

  useEffect(() => {
    // OLD CODE:
    // setFormData((prev) => ({
    //   ...prev,
    //   assets: [],
    //   pickupDate: undefined,
    //   pickupShift: null,
    // }));

    // NEW CODE: Reset dates, shifts, and assets when room changes
    setFormData((prev) => ({
      ...prev,
      assets: [],
      pickupDate: undefined,
      pickupShift: null,
      returnDate: undefined,
      returnShift: null,
    }));
  }, [formData.room]);

  useEffect(() => {
    const fetchShifts = async () => {
      if (formData.pickupDate === undefined) {
        setPickupShiftsResponse({ shifts: [], room_capacity: 0 });
        return;
      }
      const chosenRoom = formData.room;
      if (!chosenRoom) return;

      setLoading(true);
      const rawShiftsResponse = await ShiftService.getAvailableShifts(
        formData.pickupDate,
        chosenRoom.id.toString(),
        chosenRoom.room_code
      );

      setPickupShiftsResponse({
        ...rawShiftsResponse,
        shifts: rawShiftsResponse.shifts.filter(
          (shift) => shift.borrower_count < rawShiftsResponse.room_capacity
        ),
      });
      setLoading(false);
    };

    fetchShifts();
    // OLD CODE:
    // setFormData((prev) => ({
    //   ...prev,
    //   returnDate: undefined,
    //   returnShift: null,
    // }));

    // NEW CODE: Reset return date/shift and assets when pickup date changes
    setFormData((prev) => ({
      ...prev,
      returnDate: undefined,
      returnShift: null,
      assets: [],
    }));
  }, [formData.pickupDate, formData.room, setLoading]);

  useEffect(() => {
    const fetchShifts = async () => {
      if (formData.returnDate === undefined) {
        setReturnShiftsResponse({ shifts: [], room_capacity: 0 });
        return;
      }
      const chosenRoom = formData.room;
      if (!chosenRoom) return;

      setLoading(true);
      const rawShiftsResponse = await ShiftService.getAvailableShifts(
        formData.returnDate,
        chosenRoom.id.toString(),
        chosenRoom.room_code
      );

      setReturnShiftsResponse({
        ...rawShiftsResponse,
        shifts: rawShiftsResponse.shifts.filter(
          (shift) => shift.borrower_count < rawShiftsResponse.room_capacity
        ),
      });
      setLoading(false);
    };

    fetchShifts();
    // NEW CODE: Reset assets when return date changes to ensure selected assets match the new window
    setFormData((prev) => ({
      ...prev,
      assets: [],
    }));
  }, [formData.returnDate, formData.room, setLoading]);

  // OLD CODE:
  // const fetchAssets = useCallback(
  //   async ({
  //     room,
  //     currentPage,
  //     searchTerm,
  //   }: FetchAssetsParams): Promise<PaginatedResponse<IAsset[]>> => {
  //     if (room === null)
  //       return { data: [], current_page: 1, last_page: 1, per_page: 15 };
  //
  //     setLoading(true);
  //     const res = await AssetBorrowingService.getBorrowableAssetByRooms(
  //       userStore.data?.Email ?? "",
  //       "Assistant",
  //       currentPage,
  //       [parseInt(String(room.id))],
  //       searchTerm.trim() ? searchTerm.trim() : undefined
  //     );
  //     setLoading(false);
  //     return res;
  //   },
  //   [setLoading, userStore.data?.Email]
  // );

  // NEW CODE: Pass pickup/return dates and shifts so only available assets are fetched
  const fetchAssets = useCallback(
    async ({
      room,
      currentPage,
      searchTerm,
    }: FetchAssetsParams): Promise<PaginatedResponse<IAsset[]>> => {
      if (room === null)
        return { data: [], current_page: 1, last_page: 1, per_page: 15 };

      setLoading(true);
      const res = await AssetBorrowingService.getBorrowableAssetByRooms(
        userStore.data?.Email ?? "",
        "Assistant",
        currentPage,
        [parseInt(String(room.id))],
        searchTerm.trim() ? searchTerm.trim() : undefined,
        formData.pickupDate,
        formData.returnDate,
        formData.pickupShift ? Number(formData.pickupShift.id) : undefined,
        formData.returnShift ? Number(formData.returnShift.id) : undefined
      );
      setLoading(false);
      return res;
    },
    [
      setLoading,
      userStore.data?.Email,
      formData.pickupDate,
      formData.returnDate,
      formData.pickupShift,
      formData.returnShift,
    ]
  );

  const resetForm = () => {
    setFormData(initialFormData);
  };

  const validateForm = (): boolean => {
    if (
      !formData.locationId ||
      !formData.room ||
      formData.assets.length === 0 ||
      !formData.pickupDate ||
      !formData.pickupShift ||
      !formData.returnDate ||
      !formData.returnShift ||
      !formData.purpose.trim() ||
      !formData.borrowerPhoneNumber.trim() ||
      isNaN(+formData.borrowerPhoneNumber) ||
      !formData.leaderEmailOrBinusianId.trim() ||
      !formData.isResponsibilityConfirmed
    ) {
      return false;
    }
    return true;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    setLoading(true);
    if (!validateForm()) {
      toast({
        title: "Form error",
        description: "Please fill all required fields...",
      });
      setLoading(false);
      return;
    }

    try {
      // fetch leader binusian data
      const leader = await resolveBorrowerCredentials([
        formData.leaderEmailOrBinusianId,
      ]);

      const studentNimLeader = leader[0].binusian_id;
      const borrowerEmail = leader[0].email;
      const borrowerName = leader[0].name;

      // submit
      await AssetBorrowingService.submitAssetBorrowing(
        "staff",
        formData.assets,
        formData.pickupDate!,
        Number(formData.pickupShift!.id),
        formData.returnDate!,
        Number(formData.returnShift!.id),
        formData.purpose,
        formData.borrowerPhoneNumber,
        studentNimLeader,
        borrowerEmail,
        borrowerName
      );

      resetForm();
      toast({
        title: "Success.",
        description: "Asset borrowing submitted successfully.",
      });
    } catch (error) {
      if (error instanceof UserFacingError) {
        toast({
          title: error.title,
          description: error.description,
        });
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 1 }}
    >
      <ChooseAssetsDialog
        open={changeAssetsDialogOpen}
        setIsOpen={setChangeAssetsDialogOpen}
        room={formData.room}
        savedAssets={formData.assets}
        onSaveAssets={(assets) => {
          setFormData({ ...formData, assets: assets });
        }}
        fetchAssetsFunction={fetchAssets}
      />
      <form
        className="w-full rounded-lg flex flex-col items-center border-primary bg-background py-8 xl:p-16"
        onSubmit={handleSubmit}
      >
        <div className="mb-4 flex w-full items-center justify-center pb-5 flex-col gap-3 border-b-2 ">
          <h3 className="text-3xl text-center text-primary xl:text-5xl">
            Asset Borrowing
          </h3>
        </div>
        <div className="flex w-full items-start justify-center flex-col gap-3 rounded-md pb-3 mb-3 xl:text-lg">
          <Label className="font-bold text-md">
            Campus <span className="text-red-500 text-lg">*</span>
          </Label>
          <Select
            onValueChange={(value) => {
              setFormData({ ...formData, locationId: Number(value) });
            }}
          >
            <SelectTrigger className="w-full text-md">
              <SelectValue placeholder="Select Campus" />
            </SelectTrigger>
            <SelectContent>
              {locations.length === 0 && <div>No Data</div>}
              {locations.map((location) => (
                <SelectItem key={location.id} value={location.id.toString()}>
                  {location.location}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div className="flex w-full items-start justify-center flex-col gap-3 rounded-md pb-3 mb-3 xl:text-lg">
          <Label className="font-bold text-md">
            Room <span className="text-red-500 text-lg">*</span>
          </Label>
          <Select
            onValueChange={(value) => {
              setFormData({
                ...formData,
                room: rooms.find((r) => r.id === Number(value)) || null,
              });
            }}
            disabled={formData.locationId === null}
          >
            <SelectTrigger className="w-full text-md">
              <SelectValue placeholder="Select Room" />
            </SelectTrigger>
            <SelectContent>
              {rooms.length === 0 && <div>No Data</div>}
              {rooms.map((room) => (
                <SelectItem key={room.id} value={room.id.toString()}>
                  {room.room_code} - {room.room_name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        {/* OLD CODE: Assets section was previously placed here before Pickup and Return Dates
        <div className="flex w-full items-start justify-center flex-col gap-3 rounded-md pb-3 mb-3 xl:text-lg">
          <Label className="font-bold text-md">
            Assets <span className="text-red-500 text-lg">*</span>
          </Label>
          <div className="flex w-full gap-4">
            <div className="flex flex-wrap gap-2 w-full text-md border border-input bg-background px-3 py-2 rounded-md">
              {formData.assets.length === 0 ? (
                <>No assets selected</>
              ) : (
                formData.assets.map((asset) => (
                  <Badge className="text-sm" variant={"secondary"}>
                    {asset.id} - {asset.name}
                  </Badge>
                ))
              )}
            </div>
            <Button
              type="button"
              variant={"blue"}
              className="w-60 flex items-center gap-4"
              onClick={() => {
                setChangeAssetsDialogOpen(true);
              }}
              disabled={formData.room === null}
            >
              Choose Assets <BookOpenCheck />
            </Button>
          </div>
        </div>
        */}

        {/* NEW CODE: Pickup Date comes after Room selection */}
        <div className="flex w-full gap-5 rounded-md pb-3 mb-3 xl:text-lg">
          <div className="flex flex-col gap-3">
            <Label className="font-bold text-md">
              Pickup Date <span className="text-red-500 text-lg">*</span>
            </Label>
            <div className="w-full min-h-72 flex flex-col lg:flex-row gap-4">
              <div className="flex flex-col">
                <Calendar
                  className="self-center"
                  mode="single"
                  captionLayout="dropdown-buttons"
                  selected={formData.pickupDate}
                  onSelect={(date) => {
                    setFormData({
                      ...formData,
                      pickupDate: date,
                      pickupShift: null,
                      returnDate: undefined,
                      returnShift: null,
                      assets: [],
                    });
                  }}
                  // OLD CODE: disabled={formData.assets.length <= 0}
                  // NEW CODE: disabled if room is not selected yet
                  disabled={formData.room === null}
                  fromYear={new Date().getFullYear()}
                  toYear={new Date().getFullYear() + 2}
                />
              </div>
            </div>
          </div>
          <div className="flex flex-col gap-3">
            <Label className="font-bold text-md">
              Shifts <span className="text-red-500 text-lg">*</span>
            </Label>
            <h4
              className={`text-sm ${
                formData.pickupDate === undefined ? "block" : "hidden"
              }`}
            >
              Please select a date before selecting shift.
            </h4>
            <div
              className={`${
                formData.pickupDate === undefined ? "hidden" : "block"
              }`}
            >
              <h4
                className={`text-sm text-yellow-300 mb-2 ${
                  formData.pickupDate && isMatch(formData.pickupDate, holidays)
                    ? "block"
                    : "hidden"
                }`}
              >
                Warning: you are selecting a holiday, if this is unintended,
                please select another date
              </h4>
              {pickupShiftsResponse.shifts.length < 1 ? (
                <>No shifts available</>
              ) : (
                <SelectionFilter
                  options={convertShiftsToOptions(pickupShiftsResponse.shifts)}
                  handleSelected={(selected) => {
                    setFormData({
                      ...formData,
                      pickupShift:
                        pickupShiftsResponse.shifts.find(
                          (s) => selected?.id === s.id.toString()
                        ) ?? null,
                      returnDate: undefined,
                      returnShift: null,
                      assets: [],
                    });
                  }}
                  selected={
                    formData.pickupShift
                      ? {
                          id: formData.pickupShift.id.toString(),
                          name: formData.pickupShift.name,
                        }
                      : null
                  }
                  classNames={{ list: "md:grid-cols-2" }}
                />
              )}
            </div>
          </div>
        </div>

        {/* Return Date section */}
        <div className="flex w-full gap-5 rounded-md pb-3 mb-3 xl:text-lg">
          <div className="flex flex-col gap-3">
            <Label className="font-bold text-md">
              Return Date <span className="text-red-500 text-lg">*</span>
            </Label>
            <div className="w-full min-h-72 flex flex-col lg:flex-row gap-4">
              <div className="flex flex-col">
                <Calendar
                  className="self-center"
                  mode="single"
                  captionLayout="dropdown-buttons"
                  selected={formData.returnDate}
                  onSelect={(date) => {
                    setFormData({
                      ...formData,
                      returnDate: date,
                      returnShift: null,
                      assets: [],
                    });
                  }}
                  disabled={
                    !formData.pickupDate
                      ? true
                      : { before: formData.pickupDate } // can't select date before the selected pickup date
                  }
                  fromYear={new Date().getFullYear()}
                  toYear={new Date().getFullYear() + 2}
                />
              </div>
            </div>
          </div>
          <div className="flex flex-col gap-3">
            <Label className="font-bold text-md">
              Shifts <span className="text-red-500 text-lg">*</span>
            </Label>
            <h4
              className={`text-sm ${
                formData.returnDate === undefined ? "block" : "hidden"
              }`}
            >
              Please select a date before selecting shift.
            </h4>
            <div
              className={`${
                formData.returnDate === undefined ? "hidden" : "block"
              }`}
            >
              <h4
                className={`text-sm text-yellow-300 mb-2 ${
                  formData.returnDate && isMatch(formData.returnDate, holidays)
                    ? "block"
                    : "hidden"
                }`}
              >
                Warning: you are selecting a holiday, if this is unintended,
                please select another date
              </h4>
              {returnShiftsResponse.shifts.length < 1 ? (
                <>No shifts available</>
              ) : (
                <SelectionFilter
                  options={convertShiftsToOptions(returnShiftsResponse.shifts)}
                  handleSelected={(selected) => {
                    setFormData({
                      ...formData,
                      returnShift:
                        returnShiftsResponse.shifts.find(
                          (s) => selected?.id === s.id.toString()
                        ) ?? null,
                      assets: [],
                    });
                  }}
                  selected={
                    formData.returnShift
                      ? {
                          id: formData.returnShift.id.toString(),
                          name: formData.returnShift.name,
                        }
                      : null
                  }
                  classNames={{ list: "md:grid-cols-2" }}
                />
              )}
            </div>
          </div>
        </div>

        {/* NEW CODE: Assets section is now placed after Pickup and Return Dates & Shifts */}
        <div className="flex w-full items-start justify-center flex-col gap-3 rounded-md pb-3 mb-3 xl:text-lg">
          <Label className="font-bold text-md">
            Assets <span className="text-red-500 text-lg">*</span>
          </Label>
          <div className="flex w-full gap-4">
            <div className="flex flex-wrap gap-2 w-full text-md border border-input bg-background px-3 py-2 rounded-md">
              {formData.assets.length === 0 ? (
                <>No assets selected</>
              ) : (
                formData.assets.map((asset) => (
                  <Badge key={asset.id} className="text-sm" variant={"secondary"}>
                    {asset.id} - {asset.name}
                  </Badge>
                ))
              )}
            </div>
            <Button
              type="button"
              variant={"blue"}
              className="w-60 flex items-center gap-4"
              onClick={() => {
                setChangeAssetsDialogOpen(true);
              }}
              // Disabled until room, dates, and shifts are all selected
              disabled={
                formData.room === null ||
                !formData.pickupDate ||
                !formData.pickupShift ||
                !formData.returnDate ||
                !formData.returnShift
              }
            >
              Choose Assets <BookOpenCheck />
            </Button>
          </div>
        </div>

        <div className="flex w-full items-start justify-center flex-col gap-3 rounded-md pb-3 mb-3 xl:text-lg">
          <Label className="font-bold text-md">
            Purpose <span className="text-red-500 text-lg">*</span>
          </Label>
          <Input
            value={formData.purpose}
            onChange={(e) =>
              setFormData({ ...formData, purpose: e.target.value })
            }
            type="text"
            placeholder="Borrowing Purpose"
            className="w-full text-md"
            disabled={formData.assets.length === 0}
          />
        </div>
        <div className="flex w-full items-start justify-center flex-col gap-3 rounded-md pb-3 mb-3 xl:text-lg">
          <Label className="font-bold text-md">
            Phone <span className="text-red-500 text-lg">*</span>
          </Label>
          <Input
            value={formData.borrowerPhoneNumber}
            onChange={(e) =>
              setFormData({ ...formData, borrowerPhoneNumber: e.target.value })
            }
            type="tel"
            placeholder="Phone Number"
            className="w-full text-md"
            disabled={formData.assets.length === 0}
          />
        </div>
        <div className="flex w-full items-start justify-center flex-col gap-3 rounded-md pb-3 mb-3 xl:text-lg">
          <Label className="font-bold text-md">
            Leader <span className="text-red-500 text-lg">*</span>
          </Label>
          <Input
            value={formData.leaderEmailOrBinusianId}
            onChange={(e) =>
              setFormData({
                ...formData,
                leaderEmailOrBinusianId: e.target.value,
              })
            }
            placeholder="Student Email / Binusian ID"
            className="w-full text-md"
            disabled={formData.assets.length === 0}
          />
        </div>
        <BorrowingResponsibilityConfirmation
          checked={formData.isResponsibilityConfirmed}
          onCheckedChange={(checked) =>
            setFormData({
              ...formData,
              isResponsibilityConfirmed: checked,
            })
          }
          className="mb-6"
        />
        <Button
          type="submit"
          variant={"blue"}
          className="w-60 mt-6"
          disabled={!validateForm()}
        >
          Submit
        </Button>
      </form>
    </motion.div>
  );
};

export default InsertAssetBorrowingForm;
